import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  TextField,
  TextFieldProps,
} from '@mui/material';
import React, { HTMLAttributes, ReactNode } from 'react';
import { useController, useFormContext, RegisterOptions } from 'react-hook-form';
import { StyledPaper } from '../../constants/styles';

type FormTextFieldProps = TextFieldProps & {
  name: string;
  rules?: RegisterOptions;
  options: string[];
  getOptionLabel?: (o: string) => string;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: string,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  groupBy?: (option: string) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  noOptionsText?: ReactNode;
  multiple?: boolean;
};

const FormAutocomplete = ({
  name,
  rules,
  options,
  size,
  sx,
  getOptionLabel,
  renderOption,
  groupBy,
  renderGroup,
  noOptionsText,
  disabled,
  multiple = false,
  defaultValue,
  ...rest
}: FormTextFieldProps) => {
  const { field } = useController({ name, rules, defaultValue });

  const {
    formState: { errors },
    setValue,
  } = useFormContext<{ [key: string]: string | string[] }>();

  const error = errors[name];

  return (
    <Autocomplete
      id={name}
      freeSolo
      options={options}
      onChange={(_: unknown, newValue: string[] | string | null) => {
        setValue(name, newValue || (multiple ? [] : ''));
      }}
      value={field.value}
      disableClearable
      size={size}
      multiple={multiple}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      PaperComponent={StyledPaper}
      groupBy={groupBy}
      renderGroup={renderGroup}
      noOptionsText={noOptionsText}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          color="info"
          required={Boolean(rules?.required)}
          error={Boolean(error)}
          helperText={error?.message}
          sx={{
            div: {
              backgroundColor: (t) => t.palette.background.paper,
            },
            ...sx,
          }}
          {...rest}
          {...field}
          {...params}
        />
      )}
    />
  );
};

export default FormAutocomplete;
